/** CSS contract: css-library/components/01-leaves/select.css. */
import type { Ref } from 'react';
import { useSelectPopover } from '../lib/useSelectPopover';
import { ButtonControl } from './ButtonControl';

import { useEffect, useId, useRef } from 'react';
import type { ButtonHTMLAttributes, KeyboardEvent, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx';
import { mergeRefs } from '../lib/merge-refs';
import { Icon } from './Icon';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: string;
  count?: number;
  testId?: string;
  lang?: string;
}

export interface SelectProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'defaultValue' | 'onChange' | 'children'> {
  options: SelectOption[];

  value: string;

  onChange?: (value: string) => void;
  placeholder?: string;
  compact?: boolean;
  readOnly?: boolean;
  icon?: string;
}

export function Select(
  { ref, options,
    value,
    onChange,
    placeholder = '—',
    compact = false,
    readOnly = false,
    icon,
    className,
    disabled,
    id,
    onClick,
    onKeyDown,
    ...rest
  }: SelectProps & { ref?: Ref<HTMLButtonElement> },
) {
  const { open, setOpen, close, entered, container, wrapRef, triggerRef, popoverRef, style } =
    useSelectPopover<HTMLButtonElement>('select', options, compact);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listboxId = useId();
  const selected = options.find((option) => option.value === value && !option.disabled);
  useEffect(() => {
    if (!open) return;
    const selectedIndex = options.findIndex((o) => o.value === value);
    const target =
      selectedIndex >= 0 && !options[selectedIndex].disabled
        ? selectedIndex
        : options.findIndex((o) => !o.disabled);
    if (target >= 0) optionRefs.current[target]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  function focusStep(fromIndex: number, dir: 1 | -1) {
    const n = options.length;
    if (n === 0) return;
    let i = fromIndex;
    for (let step = 0; step < n; step++) {
      i = (i + dir + n) % n;
      if (!options[i].disabled) {
        optionRefs.current[i]?.focus();
        return;
      }
    }
  }

  function handleTriggerClick(e: MouseEvent<HTMLButtonElement>) {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (readOnly) return;
    setOpen((o) => !o);
  }

  function handleTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (!open && !readOnly && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setOpen(true);
    } else if (open && e.key === 'Escape') {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    }
  }

  function handleOptionKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusStep(index, 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusStep(index, -1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    }
  }

  function pick(opt: SelectOption) {
    if (opt.disabled) return;
    onChange?.(opt.value);
    close();
    triggerRef.current?.focus();
  }

  return (
    <div
      ref={wrapRef}
      className={cx('select-wrap', compact && 'select-wrap--compact', open && 'is-open', className)}
    >
      <ButtonControl
        ref={mergeRefs(triggerRef, ref)}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-readonly={readOnly || undefined}
        disabled={disabled}
        className={cx('select-display', compact && 'select-display--compact')}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        {...rest}
      >
        {icon && (
          <Icon
            name={icon}
            className="select-leading-icon"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        <span className={cx('select-value', !selected && 'is-placeholder')} lang={selected?.lang}>
          {selected ? selected.label : placeholder}
        </span>
        {selected?.count != null && <span className="select-value-count">{selected.count}</span>}
      </ButtonControl>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            id={listboxId}
            role="listbox"
            className={cx('select-popover', entered && 'is-open')}
            style={style}
          >
            {options.map((opt, index) => (
              <ButtonControl
                key={opt.value}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                type="button"
                role="option"
                aria-selected={opt.value === value}
                data-value={opt.value}
                data-testid={opt.testId}
                lang={opt.lang}
                disabled={opt.disabled}
                className={cx(
                  'select-option',
                  opt.value === value && 'is-selected',
                  opt.disabled && 'is-disabled',
                )}
                onClick={() => pick(opt)}
                onKeyDown={(e) => handleOptionKeyDown(e, index)}
              >
                {opt.icon && (
                  <Icon
                    name={opt.icon}
                    className="select-option-icon"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                {opt.label}
                {opt.count != null && <span className="select-option-count">{opt.count}</span>}
              </ButtonControl>
            ))}
          </div>,
          container ?? document.body,
        )}
    </div>
  );
}
